import { z } from "zod";
import { api, mBaseURL } from "@/config/api";
import { unwrapApiError } from "@/lib/error";
import { createURLPath } from "@/lib/utils";
import { APIResponseSuccessSchema, type APIResponseSuccess } from "@/schema/api";

import { UserSchema, type User } from "@/schema/authentication";
import { CompanyProfile, CompanyProfileSchema, CustomerProfileSchema, type CustomerProfile } from "@/schema/profile";
import { queryOptions } from "@tanstack/react-query";

function parseCompanyProfileSuccess(json: unknown) {
  const strict = APIResponseSuccessSchema.extend({
    data: CompanyProfileSchema,
  }).safeParse(json);
  if (strict.success) return strict.data;

  const loose = APIResponseSuccessSchema.extend({
    data: z
      .object({
        id: z.string(),
        organization_id: z.string(),
        created_at: z.string(),
        updated_at: z.string(),
      })
      .passthrough(),
  }).safeParse(json);
  if (loose.success) {
    return loose.data as APIResponseSuccess<CompanyProfile>;
  }

  throw new Error(`Company profile validation failed: ${strict.error.message}`);
}

function parseCustomerProfileSuccess(json: unknown) {
  const strict = APIResponseSuccessSchema.extend({
    data: CustomerProfileSchema,
  }).safeParse(json);
  if (strict.success) return strict.data;

  const loose = APIResponseSuccessSchema.extend({
    data: z
      .object({
        id: z.string(),
        user_id: z.string(),
        created_at: z.string(),
        updated_at: z.string(),
      })
      .passthrough(),
  }).safeParse(json);
  if (loose.success) {
    return loose.data as APIResponseSuccess<CustomerProfile>;
  }

  throw new Error(`Customer profile validation failed: ${strict.error.message}`);
}

export const ProfileApiFactory = {
  Endpoint: mBaseURL + "/api/profile",
  Keys: {
    FetchCompanyProfile: () => ["profile", "me", "company"],
    UpdateCompanyProfile: (organization: string) => ["profile", "company", organization],
  },
  Schemas: {
    Response: {
      User: APIResponseSuccessSchema.extend({
        data: UserSchema,
      }),
      Customer: APIResponseSuccessSchema.extend({
        data: CustomerProfileSchema,
      }),
      Company: APIResponseSuccessSchema.extend({
        data: CompanyProfileSchema,
      }),
    },
  },
  Apis: {
    FetchUserProfile: async () => {
      const response = await api(createURLPath(ProfileApiFactory.Endpoint, "me"), {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ProfileApiFactory.Schemas.Response.User.parse);
    },
    UpdateUserProfile: async (data: Partial<User>) => {
      const response = await api(createURLPath(ProfileApiFactory.Endpoint, "me"), {
        method: "PUT",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ProfileApiFactory.Schemas.Response.User.parse);
    },
    FetchCustomerProfile: async () => {
      const response = await api(createURLPath(ProfileApiFactory.Endpoint, "me", "customer"), {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(parseCustomerProfileSuccess);
    },
    UpdateCustomerProfile: async (data: Partial<CustomerProfile>) => {
      const response = await api(createURLPath(ProfileApiFactory.Endpoint, "me", "customer"), {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(parseCustomerProfileSuccess);
    },
    FetchCompanyProfile: async () => {
      const response = await api(createURLPath(ProfileApiFactory.Endpoint, "me", "company"), {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(parseCompanyProfileSuccess);
    },
    UpdateCompanyProfile: async (data: Partial<CompanyProfile>) => {
      const response = await api(createURLPath(ProfileApiFactory.Endpoint, "me", "company"), {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(parseCompanyProfileSuccess);
    },
  },
  Queries: {
    FetchCompanyProfile: () => {
      return queryOptions({
        queryKey: ProfileApiFactory.Keys.FetchCompanyProfile(),
        queryFn: ProfileApiFactory.Apis.FetchCompanyProfile,
      });
    },
  },
};
